// Shared address helpers for the 8x8 service create wizard and the 8x8 service
// edit page. Extracted verbatim from EightByEightCreate so both flows map
// addresses to/from the form and the /address payload identically.

// Maps a PingCo address record onto the form's TMF-style address shape.
export const mapAddressToForm = (addr) => ({
    street1: [addr.streetHouseNumber1, addr.streetName1, addr.streetType1]
        .filter(Boolean)
        .join(' ')
        .trim(),
    city: addr.locality ?? '',
    postCode: addr.postcode ?? '',
    stateOrProvince: addr.state ?? '',
    country: addr.country ?? '',
});

// Builds a single-line display string for the search box from an address record.
export const buildAddressDisplay = (addr, mapped) =>
    addr.description ||
    [
        mapped.street1,
        mapped.city,
        mapped.stateOrProvince,
        mapped.postCode,
        mapped.country,
    ]
        .filter(Boolean)
        .join(', ');

// Extracts the integer street number and optional alpha suffix from a token,
// handling unit/street formats like "1/317" (uses the part after the slash).
// Returns { number: string, suffix: string } e.g. "30A" → { number: "30", suffix: "A" }.
export const extractHouseNumber = (value = '') => {
    if (!value) return { number: '', suffix: '' };
    const candidate = value.includes('/') ? value.split('/').pop() : value;
    const match = candidate.match(/^(\d+)([A-Za-z]*)$/);
    if (match) {
        return { number: match[1], suffix: match[2].toUpperCase() };
    }
    // Fall back to extracting the first digit sequence (no suffix).
    const digitMatch = candidate.match(/\d+/);
    return { number: digitMatch ? digitMatch[0] : '', suffix: '' };
};

// Splits a free-text street line into a leading house number (with optional
// alpha suffix) and street name.  Only strips the first token as a house
// number when it actually yields a number, so street names that start with a
// word stay intact.
// Returns { houseNumber: string, houseNumberSuffix: string, streetName: string }.
export const parseStreetLine = (line = '') => {
    const trimmed = line.trim();
    const match = trimmed.match(/^(\S+)\s+(.*)$/);
    if (match) {
        const { number, suffix } = extractHouseNumber(match[1]);
        if (number)
            return {
                houseNumber: number,
                houseNumberSuffix: suffix,
                streetName: match[2],
            };
    }
    return { houseNumber: '', houseNumberSuffix: '', streetName: trimmed };
};

export const mapGooglePlaceToForm = (place, fallbackDisplay = '') => {
    const components = place?.address_components ?? [];
    let streetNumber = '';
    let streetName = '';
    let city = '';
    let postalTown = '';
    let adminArea2 = '';
    let state = '';
    let postCode = '';
    let country = '';

    components.forEach((c) => {
        if (c.types.includes('street_number')) streetNumber = c.long_name;
        if (c.types.includes('route')) streetName = c.long_name;
        // #2455: prefer locality; fall back to postal_town then
        // administrative_area_level_2 (common for UK addresses).
        if (c.types.includes('locality')) city = c.long_name;
        if (c.types.includes('postal_town')) postalTown = c.long_name;
        if (c.types.includes('administrative_area_level_2'))
            adminArea2 = c.long_name;
        if (c.types.includes('administrative_area_level_1'))
            state = c.long_name;
        if (c.types.includes('postal_code')) postCode = c.long_name;
        if (c.types.includes('country')) country = c.long_name;
    });

    // #2455: resolve city with fallback chain.
    const resolvedCity = city || postalTown || adminArea2;

    // #2454: extract house number and optional alpha suffix.
    let { number: houseNumber, suffix: houseNumberSuffix } =
        extractHouseNumber(streetNumber);
    if (!houseNumber) {
        const firstToken = (place?.formatted_address ?? fallbackDisplay)
            .trim()
            .split(/[\s,]/)[0];
        ({ number: houseNumber, suffix: houseNumberSuffix } =
            extractHouseNumber(firstToken));
    }
    if (!houseNumber && fallbackDisplay) {
        const firstToken = fallbackDisplay.trim().split(/[\s,]/)[0];
        ({ number: houseNumber, suffix: houseNumberSuffix } =
            extractHouseNumber(firstToken));
    }

    const street1 = [houseNumber, houseNumberSuffix, streetName]
        .filter(Boolean)
        .join(' ');

    return {
        street1,
        streetNumber: houseNumber,
        streetNumberSuffix: houseNumberSuffix,
        streetName,
        city: resolvedCity,
        postCode,
        stateOrProvince: state,
        country,
    };
};

export const buildGooglePlaceDisplay = (
    place,
    fallbackDisplay = '',
    mappedAddress = {},
) => {
    const formatted = place?.formatted_address ?? '';
    const streetNumber = mappedAddress.streetNumber ?? '';
    if (
        streetNumber &&
        fallbackDisplay.trim().startsWith(streetNumber) &&
        !formatted.trim().startsWith(streetNumber)
    ) {
        return fallbackDisplay;
    }
    return formatted || fallbackDisplay || mappedAddress.street1 || '';
};

// Resolves a country name (e.g. from Google Places) to its countryID GUID.
export const resolveCountryID = (countryName, countries) => {
    if (!countryName) return '';
    const match = (countries || []).find(
        (c) => c.name?.toLowerCase() === countryName.toLowerCase(),
    );
    return match?.id ?? '';
};

// Maps the form's TMF-style address onto a PingCo /address create payload,
// linked to the 8x8 platform.
export const mapFormToAddressPayload = (
    addr,
    { companyID, companyName, countryID },
) => {
    // Prefer the structured house number / street name captured from Google
    // Places; fall back to parsing a leading number out of the street line.
    let houseNumber = addr.streetNumber ?? '';
    // #2454: carry the alpha suffix captured from Google Places or parsing.
    let houseNumberSuffix = addr.streetNumberSuffix ?? '';
    let streetName = addr.streetName ?? '';
    if (!houseNumber || !streetName) {
        const parsed = parseStreetLine(addr.street1);
        if (!houseNumber) houseNumber = parsed.houseNumber;
        if (!houseNumberSuffix) houseNumberSuffix = parsed.houseNumberSuffix;
        if (!streetName) streetName = parsed.streetName;
    }

    // The BE requires streetHouseNumber1 to be an integer — only send it when
    // we have a numeric value, never a street-name token.
    const houseNumberInt = parseInt(houseNumber, 10);

    return {
        companyID,
        companyName: companyName ?? '',
        // Generated label for a brand-new company address (no platform address
        // existed before): "<companyName> Address".
        description: companyName ? `${companyName} Address` : 'Address',
        ...(Number.isInteger(houseNumberInt)
            ? { streetHouseNumber1: houseNumberInt }
            : {}),
        // #2454: include the suffix when present (e.g. "30A" → suffix "A").
        ...(houseNumberSuffix
            ? { streetHouseNumber1Suffix: houseNumberSuffix }
            : {}),
        streetName1: streetName,
        locality: addr.city ?? '',
        state: addr.stateOrProvince ?? '',
        postcode: addr.postCode ?? '',
        country: addr.country ?? '',
        countryID,
    };
};
